import {
  LineChartOutlined,
  ExportOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import { App, Layout, Tooltip } from 'antd';
import { Flex } from 'antd/lib';
import invariant from 'tiny-invariant';
import { useTranslation } from 'translations/hooks';

import { useAppContext } from 'business/contextProviders/useAppContext';
import { useGraphSet } from 'business/data-analysis/pages/graph/hooks/graph-context/contexts';
import { useExportGraphSet } from 'business/data-analysis/pages/graph/hooks/use-export-graph-set';
import { useMode } from 'business/data-analysis/pages/graph/hooks/use-mode';
import { GraphFilters } from 'business/data-analysis/pages/graph/types';
import { useMultipleGraphRefsContext } from 'business/providers/graph-reference';
import { handlePrintWithMultipleChart } from 'technical/handle-print-with-chart';
import Button from 'ui/button';
import { PrintButton } from 'ui/button/print-button';
import { ContentTitle } from 'ui/content-title';

import styles from './index.module.scss';

const { Header } = Layout;

interface Props {
  displaySavedGraphs: () => void;
  displaySavedGraphsButtonIsDisabled: boolean;
  setSaveSideBarVisible: (visible: boolean) => void;
  filters: GraphFilters;
}

export const GraphPageHeader = ({
  displaySavedGraphs,
  displaySavedGraphsButtonIsDisabled,
  setSaveSideBarVisible,
  filters,
}: Props) => {
  const { t } = useTranslation();
  const mode = useMode();

  const { currentConstructionSiteId } = useAppContext();
  invariant(currentConstructionSiteId, 'No construction site id');

  const { structure, displayParameterList } = useGraphSet();

  const { message } = App.useApp();

  const { generateGraphSetExportByMode, isGraphSetExportPending } =
    useExportGraphSet(message);
  const exportGraphSet = async () => {
    generateGraphSetExportByMode(
      mode,
      currentConstructionSiteId,
      structure,
      filters,
    );
  };

  const atLeastOneParameterIsSelected = Array.from(
    structure.graphs.values(),
  ).some((graph) => graph.parameters.size > 0);

  const { refs } = useMultipleGraphRefsContext();

  return (
    <Header className={styles.header}>
      <Flex gap={4}>
        <ContentTitle>
          {t('dataAnalysis.labels.title', { context: mode })}
        </ContentTitle>
        {structure.title ? (
          <ContentTitle>- {structure.title}</ContentTitle>
        ) : null}
      </Flex>
      <Flex align="center" gap={10} className={styles.headerButtons}>
        <Flex align="center" gap={4}>
          <Button
            icon={<LineChartOutlined />}
            onClick={displaySavedGraphs}
            disabled={displaySavedGraphsButtonIsDisabled}
          >
            {t('dataAnalysis.actions.savedGraph')}
          </Button>
          {displayParameterList ? (
            <Tooltip
              title={t('dataAnalysis.actions.print.tooltip')}
              placement="bottom"
            >
              <PrintButton disabled />
            </Tooltip>
          ) : (
            <PrintButton onClick={() => handlePrintWithMultipleChart(refs)} />
          )}
          <Button
            icon={<ExportOutlined />}
            onClick={exportGraphSet}
            disabled={!atLeastOneParameterIsSelected}
            loading={isGraphSetExportPending}
          >
            {t('dataAnalysis.actions.export')}
          </Button>
        </Flex>
        <Button
          onClick={() => setSaveSideBarVisible(true)}
          disabled={!atLeastOneParameterIsSelected}
          className={styles.saveGraphSetButton}
          icon={<SaveOutlined />}
        >
          {t('dataAnalysis.actions.saveGraphSet')}
        </Button>
      </Flex>
    </Header>
  );
};
